import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import classNames from "classnames";
import CiroModal from "../shared/CiroModal";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CiroCheckbox from "../shared/CiroCheckbox";
import CiroTextArea from "../shared/CiroTextArea";
import { PreviewContactFeedbackMap } from "../../contexts/InternalAutopilotHydrationContext";
import { useEffect, useState } from "react";
import { NEGATIVE_FEEDBACK_SCORE } from "../autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignContactsPreviewTable";

interface IAutopilotPreviewContactFeedbackModal {
  onClose: () => void;
  contactId?: number;
  contactName?: string;
  previewContactFeedback: PreviewContactFeedbackMap;
  setPreviewContactFeedback: (feedback: PreviewContactFeedbackMap) => void;
}

const AutopilotPreviewContactFeedbackModalSchema = yup.object({
  wrongPositionLevel: yup.boolean().default(false),
  wrongCompanyType: yup.boolean().default(false),
  missingRequiredSkills: yup.boolean().default(false),
  wrongRole: yup.boolean().default(false),
  otherReason: yup.boolean().default(false),
  additionalFeedback: yup.string().when("otherReason", {
    is: true,
    then(schema) {
      return schema.required("Please provide additional feedback");
    },
  }),
});

const AutopilotPreviewContactFeedbackModal = ({
  onClose,
  contactId,
  contactName,
  previewContactFeedback,
  setPreviewContactFeedback,
}: IAutopilotPreviewContactFeedbackModal) => {
  const [showClearFeedbackButton, setShowClearFeedbackButton] =
    useState<boolean>(false);
  const {
    watch,
    control,
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(AutopilotPreviewContactFeedbackModalSchema),
    defaultValues: {
      wrongPositionLevel: false,
      wrongCompanyType: false,
      missingRequiredSkills: false,
      wrongRole: false,
      otherReason: false,
      additionalFeedback: "",
    },
  });

  const onSubmit = (
    data: yup.InferType<typeof AutopilotPreviewContactFeedbackModalSchema>,
  ) => {
    if (!contactId) {
      throw new Error(
        "Missing contact ID in AutopilotPreviewContactFeedbackModal",
      );
    }
    const newFeedback = new Map(previewContactFeedback);
    const existingEntry = newFeedback.get(contactId);
    newFeedback.set(contactId!, {
      ...existingEntry,
      userFeedbackScore: NEGATIVE_FEEDBACK_SCORE,
      userFeedbackComment: JSON.stringify(data),
    });
    setPreviewContactFeedback(newFeedback);
    handleClose();
  };

  const handleCancel = () => {
    const newFeedback = new Map(previewContactFeedback);
    newFeedback.delete(contactId!);
    setPreviewContactFeedback(newFeedback);
    handleClose();
  };

  const handleClose = () => {
    setShowClearFeedbackButton(false);
    reset({
      wrongPositionLevel: false,
      wrongCompanyType: false,
      missingRequiredSkills: false,
      wrongRole: false,
      otherReason: false,
      additionalFeedback: "",
    });
    onClose();
  };

  useEffect(() => {
    const existingFeedback = contactId
      ? previewContactFeedback.get(contactId)
      : null;
    if (existingFeedback?.userFeedbackComment) {
      setShowClearFeedbackButton(true);
      const feedbackDetails = JSON.parse(existingFeedback.userFeedbackComment);
      reset({
        wrongPositionLevel: feedbackDetails.wrongPositionLevel,
        wrongCompanyType: feedbackDetails.wrongCompanyType,
        missingRequiredSkills: feedbackDetails.missingRequiredSkills,
        wrongRole: feedbackDetails.wrongRole,
        otherReason: feedbackDetails.otherReason,
        additionalFeedback: feedbackDetails.additionalFeedback ?? "",
      });
    }
  }, [contactId, previewContactFeedback, reset]);

  return (
    <CiroModal isOpen={Boolean(contactId)} onClose={handleClose}>
      <div className={classNames("flex", "justify-between", "items-start")}>
        <div>
          <div className={classNames("font-semibold")}>
            Why isn't {contactName ?? "this contact"} a good fit?
          </div>
          <div className="text-sm text-neutral-500">
            Your feedback helps us improve our search results.
          </div>
        </div>

        <CiroButton
          analyticsField="Close run autopilot modal"
          style={CiroButtonStyleEnum.UNSTYLED}
          onClick={handleClose}
        >
          <XMarkIcon className={classNames("w-5", "h-5")} />
        </CiroButton>
      </div>
      <div className={classNames("mt-4")}>
        <Controller
          name="wrongPositionLevel"
          control={control}
          render={({ field }) => (
            <CiroCheckbox
              className="ml-4"
              label="Wrong position level"
              checked={field.value}
              onClick={field.onChange}
            />
          )}
        />
        <Controller
          name="wrongCompanyType"
          control={control}
          render={({ field }) => (
            <CiroCheckbox
              className="ml-4"
              label="Wrong company type"
              checked={field.value}
              onClick={field.onChange}
            />
          )}
        />
        <Controller
          name="missingRequiredSkills"
          control={control}
          render={({ field }) => (
            <CiroCheckbox
              className="ml-4"
              label="Missing required skills"
              checked={field.value}
              onClick={field.onChange}
            />
          )}
        />
        <Controller
          name="wrongRole"
          control={control}
          render={({ field }) => (
            <CiroCheckbox
              className="ml-4"
              label="Wrong role"
              checked={field.value}
              onClick={field.onChange}
            />
          )}
        />
        <Controller
          name="otherReason"
          control={control}
          render={({ field }) => (
            <CiroCheckbox
              className="ml-4"
              label="Other reason"
              checked={field.value}
              onClick={field.onChange}
            />
          )}
        />
        <div className={classNames("mt-4", "text-sm")}>
          <CiroTextArea
            label={`Additional notes ${watch("otherReason") ? "" : "(optional)"}`}
            error={errors.additionalFeedback?.message}
            register={register("additionalFeedback")}
            placeholder="Tell us more about why this contact isn't a good fit..."
          />
        </div>

        <div className={classNames("mt-4", "flex", "gap-2", "w-full")}>
          <CiroButton
            customClassName={classNames("w-full")}
            analyticsField="Cancel preview contact feedback"
            style={CiroButtonStyleEnum.SECONDARY}
            onClick={handleCancel}
          >
            {showClearFeedbackButton ? "Clear Feedback" : "Cancel"}
          </CiroButton>
          <CiroButton
            customClassName={classNames("w-full")}
            analyticsField="Save preview contact feedback"
            style={CiroButtonStyleEnum.LOUD}
            onClick={handleSubmit(onSubmit)}
            disabled={false}
          >
            Submit feedback
          </CiroButton>
        </div>
      </div>
    </CiroModal>
  );
};
export default AutopilotPreviewContactFeedbackModal;
